'use client';

import React, { useEffect } from 'react';
import PageHeader from '../../components/shared/PageHeader';
import PageFooter from '../../components/shared/PageFooter';

const FORM_URL = 'https://forms.gle/MGy4JQAZ3Vapn9Vz5';

// Layout modeled on partiful.com/college-ambassadors: photo hero with the
// heading + CTA overlaid, a numbered 3-card "How it works" band, a perks
// grid, then the detail sections — all with big centered display headings.
// Copy is Sam's UW program text; palette stays Tabster navy/ombre.

const BODY_FONT ='var(--font-bricolage), -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif';

const H2: React.CSSProperties = {
  fontSize: '34px',
  fontWeight: 700,
  letterSpacing: '-0.01em',
  fontFamily: 'var(--font-bricolage), -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif',
  color: '#ffffff',
  margin: '64px 0 20px',
  textAlign: 'center',
};

const BODY: React.CSSProperties = {
  fontFamily: BODY_FONT,
  fontSize: '16px',
  lineHeight: 1.65,
  color: 'rgba(255, 255, 255, 0.85)',
  margin: '0 0 16px',
};

const LIST: React.CSSProperties = {
  margin: '0 0 16px',
  paddingLeft: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const LIST_ITEM: React.CSSProperties = {
  fontFamily: BODY_FONT,
  fontSize: '16px',
  lineHeight: 1.6,
  color: 'rgba(255, 255, 255, 0.85)',
};

const WhiteLead = ({ children }: { children: React.ReactNode }) => (
  <span style={{ color: '#ffffff', fontWeight: 700 }}>{children}</span>
);

const HOW_IT_WORKS: Array<{ num: string; title: string; body: string; gradient: string; href?: string }> = [
  {
    num: '01',
    title: 'Apply',
    href: FORM_URL,
    body: 'Your name, UW email, socials, and how you’ll use Tabster. Takes 3 minutes.',
    gradient:
      'radial-gradient(120% 90% at 15% 0%, rgba(110,231,183,0.30) 0%, rgba(110,231,183,0) 52%), ' +
      'radial-gradient(110% 80% at 90% 100%, rgba(103,232,249,0.16) 0%, rgba(103,232,249,0) 55%), ' +
      'linear-gradient(135deg, #123f3a 0%, #0e4a52 50%, #12233f 100%)',
  },
  {
    num: '02',
    title: 'Split with friends',
    body: '2+ splits a month, with at least 3 different people. Use Tabster to split a few dinners, roommate expenses, a trip, or anything else you have to settle up with friends.',
    gradient:
      'radial-gradient(120% 90% at 85% 0%, rgba(103,232,249,0.26) 0%, rgba(103,232,249,0) 52%), ' +
      'radial-gradient(110% 80% at 10% 100%, rgba(165,180,252,0.18) 0%, rgba(165,180,252,0) 55%), ' +
      'linear-gradient(135deg, #0e3a52 0%, #172d61 55%, #131c3f 100%)',
  },
  {
    num: '03',
    title: 'Post and earn',
    body: '1 reel a month, tagged @tabstertabs. If it’s high-quality enough to be posted on Tabster’s instagram, you earn that month’s U-District food credit.',
    gradient:
      'radial-gradient(120% 90% at 20% 0%, rgba(220,204,254,0.26) 0%, rgba(220,204,254,0) 52%), ' +
      'radial-gradient(110% 80% at 90% 100%, rgba(240,171,252,0.14) 0%, rgba(240,171,252,0) 55%), ' +
      'linear-gradient(135deg, #2b2354 0%, #3d2a66 50%, #1c1840 100%)',
  },
];

const PERKS: Array<{ title: string; body: string; wide?: boolean }> = [
  {
    title: '📊 Resume numbers',
    body: 'You get a dashboard showing exactly what you drove: users acquired, reel views, click-throughs. And at the end of the year, a one-page summary you can paste into your resume.',
    wide: true,
  },
  {
    title: '🍕 Free food',
    body: 'If you make a reel that is high-quality enough for Tabster to cross-post, you get a credit that month at a U-District restaurant.',
  },
  {
    title: '✨ Swag',
    body: 'Hat and stickers at onboarding.',
  },
  {
    title: '✅ LinkedIn recommendation',
    body: 'For top performers at the end of the year from the founder. Written personally, not a template.',
  },
  {
    title: '🧑‍💻 Slack channel',
    body: 'The campus ambassador squad. Fifty people at UW who all like finding new places to eat.',
  },
];

const AmbassadorsContent = () => {
  const font = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif';

  // Scroll-entrance reveals (Partiful-style): everything tagged [data-reveal]
  // slides up + fades in the first time it enters the viewport.
  useEffect(() => {
    const els = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal]'));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('amb-in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a1538 0%, #0c1740 10%, #0e1d48 25%, #102650 40%, #0e1d48 60%, #0c1740 80%, #0a1538 100%)',
      color: 'white',
      fontFamily: font,
      position: 'relative',
    }}>
      <PageHeader />
      <main className="amb-main" style={{
        maxWidth: '1080px',
        margin: '0 auto',
        padding: '24px 16px 64px',
      }}>
        <style>{`
          @keyframes ambSparkle {
            0%, 100% { background-position: 200% center; }
            50% { background-position: -200% center; }
          }
          .amb-h1 { animation: ambSparkle 6s ease-in-out infinite; }
          @keyframes ambFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @media (prefers-reduced-motion: reduce) {
            .amb-h1 { animation: none !important; }
          }

          /* Hero — full-width image band faded into the navy, same treatment
             as the homepage's DRINKS hero, but with the logged-in home's
             Contessa welcome photo. Sits absolutely behind the header; the
             vignette + bottom mask dissolve it into the page gradient. */
          .amb-hero-band {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 420px;
            overflow: hidden;
            pointer-events: none;
            animation: ambFadeIn 0.7s ease-out both;
            /* Mask the whole band (image + vignette together) so the bottom
               dissolves into whatever page-gradient color sits behind it —
               masking only the img left the vignette's solid navy painting a
               hard line at the band's bottom edge. */
            -webkit-mask-image: linear-gradient(to bottom, black 0%, black 45%, transparent 100%);
            mask-image: linear-gradient(to bottom, black 0%, black 45%, transparent 100%);
          }
          .amb-hero-band img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: 60% 62%;
            opacity: 0.65;
          }
          .amb-hero-vignette {
            position: absolute;
            inset: 0;
            background:
              radial-gradient(ellipse at center, transparent 20%, rgba(10, 21, 56, 0.4) 60%, #0a1538 90%),
              linear-gradient(to bottom, rgba(13, 15, 24, 0.5) 0%, transparent 25%, rgba(10, 21, 56, 0.25) 45%, rgba(10, 21, 56, 0.6) 70%, rgba(10, 21, 56, 0.9) 88%, #0a1538 100%),
              linear-gradient(to right, rgba(13, 15, 24, 0.4) 0%, transparent 15%, transparent 85%, rgba(13, 15, 24, 0.4) 100%);
          }
          @media (max-width: 767px) {
            .amb-hero-band { height: 340px; }
          }
          .amb-hero-content {
            position: relative;
            z-index: 2;
            max-width: 640px;
            margin: 90px auto 40px;
            text-align: center;
          }
          @media (min-width: 768px) {
            .amb-hero-content { margin-top: 140px; }
          }
          .amb-hero-h1 { font-size: 28px; }
          @media (min-width: 768px) {
            .amb-hero-h1 { font-size: 34px; }
          }
          /* Lavender-sparkle key phrases inside the hero. The parent h1's
             text-shadow must be cleared here — it bleeds through the
             transparent gradient-clipped glyphs. */
          .amb-sparkle {
            font-weight: 700;
            background: linear-gradient(90deg, #E8DEFF 0%, #E8DEFF 40%, #FFFFFF 50%, #E8DEFF 60%, #E8DEFF 100%);
            background-size: 200% auto;
            -webkit-background-clip: text;
            background-clip: text;
            -webkit-text-fill-color: transparent;
            color: transparent;
            text-shadow: none;
            animation: ambSparkle 6s ease-in-out infinite;
          }
          @media (prefers-reduced-motion: reduce) {
            .amb-sparkle { animation: none; }
          }
          /* Hero CTA — compact white pill like the Partiful campus hero,
             shown at every width. */
          .amb-apply-pill {
            font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 13px 32px;
            border-radius: 999px;
            background: #ffffff;
            color: #0a0f32;
            font-size: 16px;
            font-weight: 700;
            text-decoration: none;
            -webkit-tap-highlight-color: transparent;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
            transition: transform 0.18s ease, background 0.18s ease;
          }
          .amb-apply-pill:hover { background: #eef0f8; }
          .amb-apply-pill:active { transform: scale(0.97); }
          @media (max-width: 767px) {
          }

          /* How it works — numbered pastel cards, dark text (Partiful-style
             pastels drawn from the Tabster ombre palette). */
          .amb-steps {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 16px;
          }
          @media (max-width: 767px) {
            .amb-steps { grid-template-columns: 1fr; }
          }
          .amb-step {
            border-radius: 18px;
            padding: 24px 22px 26px;
            color: #ffffff;
            border: 1px solid rgba(255, 255, 255, 0.14);
            display: flex;
            flex-direction: column;
          }
          .amb-step .num {
            font-family: var(--font-bricolage), -apple-system, sans-serif;
            font-size: 34px;
            font-weight: 600;
            background: linear-gradient(90deg, #6ee7b7 0%, #06dffa 55%, #dcccfe 100%);
            -webkit-background-clip: text;
            background-clip: text;
            -webkit-text-fill-color: transparent;
            color: transparent;
            width: fit-content;
          }
          a.amb-step { text-decoration: none; cursor: pointer; }
          a.amb-step:hover { filter: brightness(1.15); }
          a.amb-step .title { text-decoration: underline; text-underline-offset: 3px; }
          .amb-step .title { font-family: var(--font-bricolage), -apple-system, sans-serif; font-size: 19px; font-weight: 700; margin: 14px 0 6px; }
          .amb-step .body { font-family: var(--font-bricolage), -apple-system, sans-serif; font-size: 14.5px; line-height: 1.5; color: rgba(255, 255, 255, 0.82); }

          /* Perks grid — dark glass cards. */
          .amb-perks {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
          }
          @media (max-width: 767px) {
            .amb-perks { grid-template-columns: 1fr; }
          }
          .amb-perk {
            background: rgba(255, 255, 255, 0.06);
            border-radius: 6px;
            padding: 22px 24px;
          }
          .amb-perk.wide { grid-column: 1 / -1; }
          @media (max-width: 767px) {
            .amb-perk.wide { grid-column: auto; }
          }
          .amb-perk .title { font-family: var(--font-bricolage), -apple-system, sans-serif; font-size: 17px; font-weight: 700; color: #ffffff; margin-bottom: 8px; }
          .amb-perk .body { font-family: var(--font-bricolage), -apple-system, sans-serif; font-size: 15px; line-height: 1.6; color: rgba(255, 255, 255, 0.8); }

          .amb-narrow { max-width: 760px; margin: 0 auto; }

          /* Scroll-entrance reveals. */
          [data-reveal] {
            opacity: 0;
            transform: translateY(28px);
            transition: opacity 0.7s ease, transform 0.7s ease;
          }
          [data-reveal].amb-in {
            opacity: 1;
            transform: translateY(0);
          }
          @media (prefers-reduced-motion: reduce) {
            [data-reveal] {
              opacity: 1 !important;
              transform: none !important;
              transition: none !important;
            }
          }

          .amb-link {
            background: linear-gradient(90deg, #6ee7b7 0%, #06dffa 45%, #dcccfe 100%);
            -webkit-background-clip: text;
            background-clip: text;
            -webkit-text-fill-color: transparent;
            color: transparent;
            font-weight: 600;
            text-decoration: underline;
            text-decoration-color: #06dffa;
            text-underline-offset: 3px;
          }
        `}</style>

        {/* Hero */}
        <div className="amb-hero-band" aria-hidden="true">
          <img src="/Contessa.jpg" alt="" fetchPriority="high" />
          <div className="amb-hero-vignette" />
        </div>
        <div className="amb-hero-content">
            <h1 className="amb-hero-h1" style={{
              fontWeight: 500,
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
              fontFamily: 'var(--font-bricolage), -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif',
              color: '#ffffff',
              textShadow: '0 1px 3px rgba(0, 0, 0, 0.55), 0 4px 20px rgba(0, 0, 0, 0.65)',
              margin: '0 0 24px',
            }}>
              <span className="amb-sparkle">Boost your resume</span> and{' '}
              <span className="amb-sparkle">earn restaurant credits</span> for
              splitting dinners with friends.
            </h1>
            <a className="amb-apply-pill" href={FORM_URL} target="_blank" rel="noopener noreferrer">
              Apply Now
            </a>
        </div>

        {/* How it works */}
        <h2 style={H2} data-reveal>How it works</h2>
        <div className="amb-steps">
          {HOW_IT_WORKS.map((step, i) => {
            const Card: 'a' | 'div' = step.href ? 'a' : 'div';
            return (
              <Card
                key={step.num}
                className="amb-step"
                data-reveal
                href={step.href}
                target={step.href ? '_blank' : undefined}
                rel={step.href ? 'noopener noreferrer' : undefined}
                style={{ background: step.gradient, transitionDelay: `${i * 120}ms` }}
              >
                <div className="num">{step.num}</div>
                <div className="title">{step.title}{step.href ? <>&nbsp;&#8599;</> : null}</div>
                <div className="body">{step.body}</div>
              </Card>
            );
          })}
        </div>

        {/* What you get */}
        <h2 style={H2} data-reveal>What you get</h2>
        <div className="amb-perks">
          {PERKS.map((perk, i) => (
            <div
              key={perk.title}
              className={`amb-perk${perk.wide ? ' wide' : ''}`}
              data-reveal
              style={{ transitionDelay: `${i * 90}ms` }}
            >
              <div className="title">{perk.title}</div>
              <div className="body">{perk.body}</div>
            </div>
          ))}
        </div>

        {/* How to join */}
        <div className="amb-narrow">
          <h2 style={H2} data-reveal>How to join</h2>
          <p style={{ ...BODY, margin: '24px 0' }}>
            <a className="amb-apply-pill" href={FORM_URL} target="_blank" rel="noopener noreferrer">
              Apply Now
            </a>
          </p>
          <p style={BODY}>
            We&rsquo;re less interested in follower counts than in your
            enthusiasm for Tabster as a product, to use for splitting dinners
            or other expenses. Regular dinner plans or roommate expenses beat
            10,000 followers.
          </p>
          <p style={BODY}>
            Questions: DM{' '}
            <a className="amb-link" href="https://www.instagram.com/tabstertabs">@tabstertabs</a>{' '}
            or email{' '}
            <a className="amb-link" href="mailto:support@tabster.ai">support@tabster.ai</a>.
          </p>

          <h2 style={H2} data-reveal>The fine print</h2>
          <ul style={{ ...LIST, gap: '10px' }}>
            <li style={LIST_ITEM}>You must be 18 or older and currently enrolled at UW.</li>
            <li style={LIST_ITEM}>
              The crew is a rewards program, not a job. There&rsquo;s no
              schedule, no required hours, and no obligation to post. You
              choose what you do and when.
            </li>
            <li style={LIST_ITEM}>Either of us can end the arrangement any time.</li>
            <li style={LIST_ITEM}>
              Disclose. Anything you post as crew needs a clear disclosure
              inside the post itself &mdash; spoken or on-screen text, not
              just the caption. We&rsquo;ll show you exactly how.
            </li>
            <li style={LIST_ITEM}>
              Use commercially licensed audio (your platform&rsquo;s
              commercial sounds library) so we can reshare what you make. Get
              an okay from anyone identifiable who appears in it.
            </li>
            <li style={LIST_ITEM}>
              You give Tabster permission to reshare, edit, and use your crew
              content, including in ads, with credit.
            </li>
            <li style={LIST_ITEM}>
              Events: we don&rsquo;t fund alcohol. If we&rsquo;re supporting
              something you&rsquo;re throwing, you&rsquo;re the host and
              it&rsquo;s your event. Follow UW&rsquo;s policies on commercial
              activity, tabling, and residence halls.
            </li>
          </ul>
        </div>
      </main>
      <PageFooter />
    </div>
  );
};

export default AmbassadorsContent;
